/**
 * LegacyTabRedirect -- conditional redirect for a tab that moved to another page.
 *
 * FEAT-035 TASK-006 (SPEC 7.1, design.md B.3 R1). When the search param
 * `when.param` is one of `when.values`, navigates (replace) to `to`, preserving the
 * other source query params; destination params take precedence on conflict
 * (same merge rule as RedirectWithParams). Otherwise renders `children`, so the
 * other tabs of the wrapped hub are untouched.
 *
 * Example: /admin/models?tab=providers&foo=1 (any id of MOVED_TAB_IDS.models)
 *       -> /admin/connections/providers?tab=providers&foo=1
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

interface LegacyTabRedirectProps {
  /** Search param + values that trigger the redirect (e.g. MOVED_TAB_IDS.models). */
  when: { param: string; values: readonly string[] };
  /** Destination path, may include a query string. */
  to: string;
  children: ReactNode;
}

export function LegacyTabRedirect({ when, to, children }: LegacyTabRedirectProps) {
  const location = useLocation();
  const sourceParams = new URLSearchParams(location.search);
  const current = sourceParams.get(when.param);

  if (current === null || !when.values.includes(current)) {
    return <>{children}</>;
  }

  const qIdx = to.indexOf('?');
  const destPath = qIdx >= 0 ? to.slice(0, qIdx) : to;
  const destParams = new URLSearchParams(qIdx >= 0 ? to.slice(qIdx + 1) : '');

  const merged = new URLSearchParams();
  sourceParams.forEach((value, key) => {
    merged.set(key, value);
  });
  destParams.forEach((value, key) => {
    merged.set(key, value); // destination takes precedence
  });

  const search = merged.toString();
  return <Navigate to={search ? `${destPath}?${search}` : destPath} replace />;
}

export default LegacyTabRedirect;
